import { type ReactNode, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { isBase64EncodedImage, tr, useDelayedExecute } from 'mmfintech-commons'
import { configuration, useBanksRequest, useEventTracker } from 'mmfintech-checkout-commons'

import BackButton from '@components/BackButton'

import { CustomSearch } from '@components/CustomInput'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import { BanksContainer, BanksMessage, VoucherTitle } from './Blixcard.styled'
import { CheckoutVoucherLoading, CustomBankSelectItem } from '@components'

import type { SupportedBank, UseAdditionalFieldsOutput, UseFilteredBanksOutput } from 'mmfintech-checkout-commons/types'

import BankGroupIcon from '../../../assets/icons/bank-group.png'
import NoLogoBankIcon from '../../../assets/icons/no-logo-bank.png'

type BlixcardBanksStepProps = {
  countryCode: string
  paymentMethod: string
  filteredBanks: UseFilteredBanksOutput
  fieldValues: UseAdditionalFieldsOutput
  handleBack: () => void
  handleNext: () => void
  sessionId: string
}

export const BlixcardBanksStep = (props: BlixcardBanksStepProps) => {
  const { countryCode, paymentMethod, filteredBanks, fieldValues, handleBack, handleNext, sessionId } = props

  const { checkoutPayError, checkoutPayFetching } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching } }) => ({
      checkoutPayError,
      checkoutPayFetching
    }),
    shallowEqual
  )

  const [eventLogged, setEventLogged] = useState(false)

  const { trackEvent } = useEventTracker()
  const { banksCount, banksError, banksFetching } = useBanksRequest()

  const extractName = (bank: SupportedBank): string =>
    ['JPN', 'KOR'].includes(bank.countryCode) && bank.nameEnglish ? bank.nameEnglish : bank.name
  const extractDescription = (bank: SupportedBank): string =>
    ['JPN', 'KOR'].includes(bank.countryCode) && bank.nameEnglish ? bank.name : null

  const hasOptions = (bank: SupportedBank): boolean => Array.isArray(bank.options) && bank.options.length > 0

  const prepareIcon = (bank: SupportedBank): ReactNode => {
    if (bank.logoUrl) {
      return (
        <img
          src={
            isBase64EncodedImage(bank.logoUrl) || bank.logoUrl.substring(0, 4) === 'http'
              ? bank.logoUrl
              : configuration.readBackendConfig() + bank.logoUrl
          }
          alt=''
        />
      )
    }

    return <img src={hasOptions(bank) ? BankGroupIcon : NoLogoBankIcon} alt='' />
  }

  const logBankSearchFocus = () => trackEvent(sessionId, 'bank_search_focus', null, true)

  const logBankSearchType = () => {
    if (!eventLogged) {
      void trackEvent(sessionId, 'bank_search_type', null, true)
      setEventLogged(true)
    }
  }

  const delayedLog = useDelayedExecute({
    onExecute: () => {
      void trackEvent(sessionId, 'banks_scrolling', '', true)
    }
  })

  const handleSelect = (bank: SupportedBank): void => {
    if (!bank.underMaintenance || hasOptions(bank)) {
      filteredBanks.handleSelectBank(bank)
    }
  }

  const handleSubmit = () => {
    fieldValues.setRequired('bankChoiceId', true)
    handleNext()
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: add/remove listeners on mount/unmount
  useEffect(() => {
    const handleScroll = () => {
      delayedLog.execute()
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight) {
        void trackEvent(sessionId, 'banks_scrolled_to_bottom', '', true)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <>
      {typeof handleBack === 'function' ? (
        <div className='mb-2'>
          <BackButton onClick={handleBack} />
        </div>
      ) : null}

      <VoucherTitle data-test={`pay-with-${paymentMethod}-banks`}>
        {tr('CHECKOUT.VOUCHER.BANKS.TITLE', 'Pay with {{METHOD}}', {
          METHOD: tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)
        })}
      </VoucherTitle>

      <ErrorDisplay error={[checkoutPayError, banksError]} />

      {banksFetching ? (
        <CheckoutVoucherLoading>
          <Spinner />
        </CheckoutVoucherLoading>
      ) : (
        <div>
          {paymentMethod && banksCount > 10 ? (
            <div className='bank-search'>
              <CustomSearch
                key='bank-search'
                name='bank-search'
                data-test='bank-search-input'
                maxLength={50}
                label={
                  countryCode === 'DEU'
                    ? tr('CHECKOUT.PAYMENT.BANK_SEARCH_DEU', 'Search by bank name or BLZ')
                    : tr('CHECKOUT.PAYMENT.BANK_SEARCH', 'Search by bank name')
                }
                value={filteredBanks.bankSearchText}
                onChange={(_name: string, value: string) => {
                  filteredBanks.setBankSearchText(value)
                  logBankSearchType()
                }}
                onFocus={logBankSearchFocus}
                autoComplete='off'
              />
            </div>
          ) : null}

          <BanksContainer>
            {filteredBanks.hasBack ? (
              <CustomBankSelectItem
                back
                selected
                data-test='banks-back'
                onClick={filteredBanks.handleBack}
                caption={extractName(filteredBanks.selectedGroup)}
                description={extractDescription(filteredBanks.selectedGroup)}
              />
            ) : null}

            {Array.isArray(filteredBanks.banks) && filteredBanks.banks.length ? (
              filteredBanks.banks.map((bank: SupportedBank, index: number) => {
                const { bankChoiceId, underMaintenance } = bank
                const selected = fieldValues.getValue('bankChoiceId') === bankChoiceId
                return (
                  <CustomBankSelectItem
                    key={bankChoiceId}
                    data-test={`bank-${bankChoiceId}`}
                    caption={extractName(bank)}
                    description={extractDescription(bank)}
                    selected={selected}
                    onClick={() => (selected ? handleSubmit() : handleSelect(bank))}
                    icon={prepareIcon(bank)}
                    loading={checkoutPayFetching}
                    maintenance={underMaintenance}
                    preselected={
                      index < filteredBanks.banks.length - 1 &&
                      filteredBanks.banks[index + 1].bankChoiceId === fieldValues.getValue('bankChoiceId')
                    }
                    className={filteredBanks.hasBack ? 'bank-inner' : ''}
                  />
                )
              })
            ) : !filteredBanks.treeMode ? (
              <BanksMessage>
                {filteredBanks.bankSearchText?.length > 2
                  ? tr('CHECKOUT.PAYMENT.NO_BANKS_FOUND', 'No banks found')
                  : tr('CHECKOUT.PAYMENT.SEARCH_BANK_HINT', 'Type 3 or more characters to filter banks…')}
              </BanksMessage>
            ) : null}
          </BanksContainer>
        </div>
      )}
    </>
  )
}
